import { iHolidayQuoteItineraryBlock } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { formatConsistentDate } from "@/utils/timezone-date-utils";
import { ItineraryIcons } from "./itinerary-blocks/timeline-container";
import { usePrimaryColorStyles } from "./contexts/use-primary-color-styles";
import { useQuoteTheme } from "./quote-theme/use-quote-theme";
import { airportList } from "./helpers/airport-list";
import { scrollToItineraryBlock } from "./helpers/scroll-to-itinerary-block";
import { cn } from "@/lib/utils";

export type ItinerarySummaryVariant = "default" | "compact-strip";

interface ItinerarySummaryProps {
  itineraryBlocks: iHolidayQuoteItineraryBlock[];
  variant?: ItinerarySummaryVariant;
  className?: string;
}

function getIconForType(block: iHolidayQuoteItineraryBlock) {
  const iconName =
    block.flightData?.icon ||
    block.accommodationData?.icon ||
    block.cruiseData?.icon ||
    block.carHireData?.icon ||
    block.transferData?.icon ||
    block.attractionData?.icon ||
    block.excursionData?.icon ||
    block.railData?.icon;

  if (iconName && ItineraryIcons[iconName]) {
    return ItineraryIcons[iconName];
  }

  switch (block.type) {
    case "flight":
      return ItineraryIcons.plane;
    case "hotel":
      return ItineraryIcons.hotel;
    case "cruise":
      return ItineraryIcons.ship;
    case "car":
      return ItineraryIcons.car;
    case "transfer":
      return ItineraryIcons.bus;
    case "attraction":
      return ItineraryIcons.sightseeing;
    case "excursion":
      return ItineraryIcons.activity;
    case "rail":
      return ItineraryIcons.train;
    default:
      return ItineraryIcons.location;
  }
}

function getSummaryText(block: iHolidayQuoteItineraryBlock) {
  switch (block.type) {
    case "flight": {
      if (!block.flightData) return "";

      const flightData = block.flightData;
      const departureAirportDetails = airportList.find(
        (a) => a.iata_code === flightData.departureAirport
      );
      const arrivalAirportDetails = airportList.find(
        (a) => a.iata_code === flightData.arrivalAirport
      );
      const formattedDepartureAirport = departureAirportDetails
        ? `${departureAirportDetails.name} (${flightData.departureAirport})`
        : flightData.departureAirport;
      const formattedArrivalAirport = arrivalAirportDetails
        ? `${arrivalAirportDetails.name} (${flightData.arrivalAirport})`
        : flightData.arrivalAirport;

      return `${formattedDepartureAirport} → ${formattedArrivalAirport}`;
    }
    case "hotel":
      return block.accommodationData?.accommodationName || "";
    case "cruise":
      return block.cruiseData
        ? `${block.cruiseData.departurePort} → ${block.cruiseData.arrivalPort}`
        : "";
    case "car":
      return block.carHireData?.carType || "";
    case "transfer":
      return block.transferData
        ? `${block.transferData.pickUpLocation} → ${block.transferData.dropOffLocation}`
        : "";
    case "attraction":
      return block.attractionData?.attractionName || "";
    case "excursion":
      return block.excursionData?.excursionName || "";
    case "rail":
      return block.railData
        ? `${block.railData.departureStation} → ${block.railData.arrivalStation}`
        : "";
    default:
      return "";
  }
}

function getDate(block: iHolidayQuoteItineraryBlock) {
  switch (block.type) {
    case "flight":
      return block.flightData?.departureDate;
    case "hotel":
      return block.accommodationData?.rooms[0]?.checkInDate;
    case "cruise":
      return block.cruiseData?.departureDate;
    case "car":
      return block.carHireData?.pickUpDate;
    case "transfer":
      return block.transferData?.transferDate;
    case "attraction":
      return block.attractionData?.date;
    case "excursion":
      return block.excursionData?.date;
    case "rail":
      return block.railData?.departureDate;
    default:
      return null;
  }
}

export function ItinerarySummary({
  itineraryBlocks,
  variant = "default",
  className,
}: ItinerarySummaryProps) {
  const { containerStyle, textIconStyle } = usePrimaryColorStyles();
  const { classNames } = useQuoteTheme();

  if (itineraryBlocks.length === 0) return null;

  const summaryContainerClassName = cn(
    className ?? classNames.itinerarySummary
  );

  if (variant === "compact-strip") {
    return (
      <div
        className={cn(
          summaryContainerClassName,
          "w-full min-w-0 max-w-full overflow-hidden"
        )}
      >
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Trip overview
        </h3>
        <div className="flex min-w-0 gap-3 overflow-x-auto overscroll-x-contain pb-1 [-webkit-overflow-scrolling:touch]">
          {itineraryBlocks.map((block) => {
            const Icon = getIconForType(block);
            const summaryText = getSummaryText(block);
            const date = getDate(block);

            return (
              <button
                key={block.id}
                type="button"
                onClick={() => scrollToItineraryBlock(block.id)}
                className="w-[200px] shrink-0 cursor-pointer rounded-lg border border-gray-100 bg-white p-3 text-left transition-colors hover:bg-gray-50 sm:w-[220px]"
              >
                <div className="mb-2 flex items-center gap-2">
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-full"
                    style={containerStyle}
                  >
                    <Icon className="h-3.5 w-3.5" style={textIconStyle} />
                  </div>
                  <span className="text-xs font-medium capitalize text-gray-500">
                    {block.type}
                  </span>
                </div>
                <p className="line-clamp-2 text-sm font-medium text-gray-900">
                  {summaryText}
                </p>
                {date ? (
                  <p className="mt-1 text-xs text-gray-500">
                    {formatConsistentDate(date, "dd MMM yyyy")}
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={summaryContainerClassName}>
      <h3 className="mb-2 text-lg font-semibold sm:mb-3">Itinerary Summary</h3>
      <div className="space-y-1 sm:space-y-1.5">
        {itineraryBlocks.map((block) => {
          const Icon = getIconForType(block);
          const summaryText = getSummaryText(block);
          const date = getDate(block);

          return (
            <button
              key={block.id}
              type="button"
              onClick={() => scrollToItineraryBlock(block.id)}
              className="w-full cursor-pointer rounded-lg p-2 text-left transition-colors duration-200 hover:bg-gray-50"
            >
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="mt-0.5 flex-shrink-0">
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-full sm:h-8 sm:w-8"
                    style={containerStyle}
                  >
                    <Icon
                      className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                      style={textIconStyle}
                    />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="sm:hidden">
                    {date ? (
                      <div className="mb-1 text-sm font-medium text-gray-600">
                        {formatConsistentDate(date, "dd MMM yyyy")}
                      </div>
                    ) : null}
                    <div className="mb-0.5 text-sm font-medium leading-tight text-gray-900">
                      {summaryText}
                    </div>
                    <div className="text-xs capitalize text-gray-500">
                      {block.type}
                    </div>
                  </div>

                  <div className="hidden items-start justify-between gap-2 sm:flex sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-0.5 text-sm font-medium leading-tight text-gray-900">
                        {summaryText}
                      </div>
                      <div className="text-xs capitalize text-gray-500">
                        {block.type}
                      </div>
                    </div>
                    {date ? (
                      <div className="flex-shrink-0 self-start text-sm font-medium text-gray-600">
                        {formatConsistentDate(date, "dd MMM yyyy")}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
